/**
 * MarkdownRenderer Component
 *
 * Renders Markdown content with syntax highlighting for code blocks.
 * Used in template editor preview and diff views.
 *
 * @module components/common/MarkdownRenderer
 */

import { memo, Fragment, Children, isValidElement } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Regex for XML tags: <tag>, </tag>, <tag/>, <tag attr="val">
const XML_TAG_REGEX = /<\/?[a-zA-Z][\w-]*(?:\s+[\w-]+(?:=(?:"[^"]*"|'[^']*'|[^\s>]*))?)*\s*\/?>/g;
// Regex for template variables: {{VAR_NAME}}
const VARIABLE_REGEX = /\{\{[A-Z_][\w]*\}\}/g;
// Combined regex for both patterns (XML tags and variables)
const HIGHLIGHT_REGEX = new RegExp(
  `(${XML_TAG_REGEX.source})|(${VARIABLE_REGEX.source})`,
  'g'
);

/**
 * Processes text children to highlight XML tags and template variables.
 * Returns original children if no highlighting needed.
 */
function highlightSpecialTokens(children) {
  if (!children) return children;

  return processChildren(children);
}

function processChildren(children) {
  if (typeof children === 'string') {
    return highlightString(children);
  }
  if (Array.isArray(children)) {
    return children.map((child, i) => {
      if (typeof child === 'string') {
        const result = highlightString(child);
        // If unchanged, return as-is
        if (result === child) return child;
        return <Fragment key={i}>{result}</Fragment>;
      }
      return child;
    });
  }
  return children;
}

function highlightString(text) {
  HIGHLIGHT_REGEX.lastIndex = 0;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = HIGHLIGHT_REGEX.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const xmlTag = match[1];
    const variable = match[2];

    if (xmlTag) {
      parts.push(
        <span key={match.index} className="px-1 py-0.5 bg-blue-500/10 text-blue-400 rounded text-sm font-mono">
          {xmlTag}
        </span>
      );
    } else if (variable) {
      parts.push(
        <span key={match.index} className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs font-mono font-bold">
          {variable}
        </span>
      );
    }

    lastIndex = HIGHLIGHT_REGEX.lastIndex;
  }

  // No matches found -- return original string
  if (parts.length === 0) return text;

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

/**
 * Custom pre block renderer with syntax highlighting.
 * In react-markdown v9+, code blocks are rendered as <pre><code>...</code></pre>.
 * The `inline` prop on code was removed, so we handle blocks via `pre`.
 */
const PreBlock = memo(function PreBlock({ children, ...props }) {
  // Extract the <code> child element from <pre>
  const codeChild = Children.toArray(children).find(
    (child) => isValidElement(child) && child.type === 'code'
  );

  if (isValidElement(codeChild)) {
    const className = codeChild.props.className || '';
    const match = /language-(\w+)/.exec(className);
    const language = match ? match[1] : 'text';
    const code = String(codeChild.props.children).replace(/\n$/, '');

    return (
      <SyntaxHighlighter
        style={oneDark}
        language={language}
        PreTag="div"
        className="rounded-lg !bg-gray-900 !my-2"
      >
        {code}
      </SyntaxHighlighter>
    );
  }

  // Fallback: render as regular pre with proper formatting
  return <pre className="whitespace-pre-wrap bg-gray-900 text-gray-300 p-4 rounded-lg my-2 font-mono text-sm overflow-x-auto" {...props}>{children}</pre>;
});

/**
 * Inline code renderer (react-markdown v10: code component only receives inline code)
 */
const InlineCode = memo(function InlineCode({ children, ...props }) {
  return (
    <code className="px-1.5 py-0.5 bg-gray-800 text-pink-400 rounded text-sm font-mono" {...props}>
      {children}
    </code>
  );
});

/**
 * Custom components for ReactMarkdown
 */
const markdownComponents = {
  pre: PreBlock,
  code: InlineCode,
  // Headers
  h1: ({ children }) => <h1 className="text-2xl font-bold text-white mt-6 mb-4 pb-2 border-b border-gray-700">{children}</h1>,
  h2: ({ children }) => <h2 className="text-xl font-bold text-white mt-5 mb-3">{children}</h2>,
  h3: ({ children }) => <h3 className="text-lg font-semibold text-white mt-4 mb-2">{children}</h3>,
  h4: ({ children }) => <h4 className="text-base font-semibold text-gray-200 mt-3 mb-2">{children}</h4>,
  // Paragraphs and text (with XML-tag and variable highlighting)
  p: ({ children }) => <p className="text-gray-300 my-2 leading-relaxed">{highlightSpecialTokens(children)}</p>,
  strong: ({ children }) => <strong className="font-bold text-white">{children}</strong>,
  em: ({ children }) => <em className="italic text-gray-200">{children}</em>,
  // Lists
  ul: ({ children }) => <ul className="list-disc list-inside my-2 space-y-1 text-gray-300">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside my-2 space-y-1 text-gray-300">{children}</ol>,
  li: ({ children }) => <li className="text-gray-300">{highlightSpecialTokens(children)}</li>,
  // Links
  a: ({ href, children }) => (
    <a href={href} className="text-indigo-400 hover:text-indigo-300 underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  // Blockquote (children are React elements like <p>, which handle their own highlighting)
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-indigo-500 pl-4 my-3 text-gray-400 italic">
      {children}
    </blockquote>
  ),
  // Horizontal rule
  hr: () => <hr className="my-6 border-gray-700" />,
  // Tables
  table: ({ children }) => (
    <div className="overflow-x-auto my-4">
      <table className="min-w-full border border-gray-700 rounded-lg overflow-hidden">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-800">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-y divide-gray-700">{children}</tbody>,
  tr: ({ children }) => <tr className="hover:bg-gray-800/50">{children}</tr>,
  th: ({ children }) => <th className="px-4 py-2 text-left text-sm font-semibold text-gray-200">{children}</th>,
  td: ({ children }) => <td className="px-4 py-2 text-sm text-gray-300">{children}</td>,
};

/**
 * MarkdownRenderer Component
 *
 * @param {object} props - Component props
 * @param {string} props.content - Markdown content to render
 * @param {string} [props.className] - Additional CSS classes
 */
const MarkdownRenderer = memo(function MarkdownRenderer({ content, className = '' }) {
  if (!content) {
    return (
      <div className={`text-gray-500 italic ${className}`}>
        No content to display
      </div>
    );
  }

  return (
    <div className={`max-w-none ${className}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {content}
      </ReactMarkdown>
    </div>
  );
});

export default MarkdownRenderer;
export { markdownComponents, PreBlock, InlineCode };
